// ============================================================
// retry - exponential backoff for a write that may deserve a second go.
//
// WHICH errors deserve one is the backend's knowledge, not this file's:
// Firestore says `code: 'unavailable'`, PocketBase answers 429 or a dead
// socket, and the old `firestoreRetry` knew only the first, so on the
// self-hosted deployment a throttled write died on its first attempt
// and the player was told their change "may not have reached the
// server" (v8.310.1). The caller now supplies `retryDelay`, which reads
// the error and answers "wait this long" or null for "stop".
// ============================================================
import { randomFraction } from './entropy';

/** How long to wait before attempt `attempt + 1` after `err`, or null to give up. */
export type RetryDelay = (err: unknown, attempt: number) => number | null;

/**
 * Jitter (50–100% of the step) prevents synchronized retry stampedes
 * from several clients in one outage - and they ARE in one outage,
 * since a table shares a server. From `entropy`, NOT Math.random():
 * retries only fire under load, so this stole a die from the E2E
 * dice sequence in full sweeps and not in isolated runs, which
 * looked for all the world like flakiness.
 */
function jitteredWait(delay: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delay * (0.5 + randomFraction() * 0.5)));
}

/** After a failed attempt: rethrow when there is no next one, else wait for it. */
async function waitOrThrow(err: unknown, delay: number | null): Promise<void> {
  if (delay === null) throw err;
  await jitteredWait(delay);
}

export async function withRetry<T>(fn: () => Promise<T>, retryDelay: RetryDelay, maxRetries = 3): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      await waitOrThrow(err, attempt === maxRetries ? null : retryDelay(err, attempt));
    }
  }
  throw lastError;
}
